// Live hotfix (Oct 20, 2026): quotes start from Amanda's own prices.
// Source: "Ghostwriter Events - Pricing Sheet.pdf" (Oct 15, 2026).
// The AI only prices extras that fall outside her package and add-ons, checked against a
// Central Coast market-rate table that lives in Settings (defaults in market-rates.js).
// Setting names and ids match Phase 11's packages.js, so saved prices carry over.

import { DEFAULT_MARKET_RATES } from "./market-rates.js";

export const DEFAULT_PACKAGES = [
  {
    id: "wedding-day",
    name: "Wedding Day Coordination",
    price: 3400,
    priceFrom: null,
    onSiteHours: 8,
    inclusions: [
      "Confirmation calls with booked vendors",
      "Venue coordination",
      "Day-of point of contact for all vendors",
      "Ceremony cues and flow",
      "Setup oversight",
      "Breakdown coordination",
      "Up to 8 hours on site"
    ],
    notIncluded: "Vendor sourcing, building the wedding day timeline, rehearsal, planning meetings, design, budget management"
  },
  {
    id: "partial",
    name: "Partial Planning & Coordination",
    price: 8500,
    priceFrom: null,
    onSiteHours: 10,
    inclusions: [
      "Everything in Wedding Day Coordination",
      "Sourcing of remaining vendors",
      "Primary vendor contact and logistics",
      "Contract review (logistics only)",
      "Wedding day timeline build-out",
      "Rehearsal coordination",
      "Monthly planning meetings",
      "Up to 10 hours on site"
    ],
    notIncluded: "Design and styling, wedding budget management, venue selection, full vendor management"
  },
  {
    id: "full-service",
    name: "Full-Service Planning & Design",
    price: null, // custom: priced to guest count, timeline and scope
    priceFrom: 14000,
    onSiteHours: 10,
    inclusions: [
      "Everything in Partial Planning",
      "Venue selection assistance",
      "Vendor curation and recommendations",
      "Full vendor management",
      "Wedding budget management",
      "Vision and design consultation",
      "Monthly planning meetings, unlimited email support",
      "Day-of lead coordination"
    ],
    notIncluded: "Anything beyond these inclusions is priced as an add-on or a custom extra"
  }
];

export const DEFAULT_ADDONS = [
  { id: "extra-hours", name: "Additional on-site hours", rate: 175, unit: "hour" },
  { id: "extra-coordinator", name: "Extra on-site coordinator", rate: 350, unit: "person" },
  { id: "rehearsal", name: "Rehearsal only (standalone)", rate: 450, unit: "flat", includedIn: ["partial", "full-service"] },
  { id: "vendor-sourcing", name: "Vendor sourcing only", rate: 1200, unit: "flat", includedIn: ["partial", "full-service"] },
  { id: "guest-lodging", name: "Guest lodging coordination", rate: 750, unit: "flat" },
  { id: "vows-toasts", name: "Vow and toast writing help", rate: 350, unit: "flat" }
];

// The old Phase 10 tier names (and the questionnaire's choices) mapped to her packages.
const TIER_TO_PACKAGE = {
  "Full Service": "full-service",
  "Partial Planning": "partial",
  "Month-of Coordination": "wedding-day",
  "Day-of Coordination": "wedding-day",
  "Month-of": "wedding-day",
  "Day-of": "wedding-day"
};
export const packageIdForTier = tier => TIER_TO_PACKAGE[String(tier || "").trim()] || null;

// Settings text that predates her contract. Replaced on load when it's still the untouched old default.
export const OLD_DEFAULT_TERMS = {
  paymentTerms: "50% non-refundable retainer due at booking; remaining balance due 30 days before the event date.",
  cancellationPolicy: "Cancellations more than 90 days before the event forfeit the retainer; within 90 days, the full balance is due."
};
export const DEFAULT_TERMS_TEXT = {
  paymentTerms: "A 50% retainer holds your date, due within 14 days of signing the agreement. The balance is due 30 days before the wedding.",
  cancellationPolicy: "The retainer is non-refundable. If you cancel more than 30 days before the wedding, nothing further is owed; within 30 days, the full fee is due. One postponement to a date within 12 months is free."
};

const money = v => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v.replace(/[$,\s]/g, "")) : Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};

/** Her prices with any Settings overrides applied. Never throws on odd saved data. */
export function pricingFrom(settings) {
  const s = settings || {};
  const savedPk = Array.isArray(s.packages) ? s.packages : [];
  const savedAo = Array.isArray(s.addons) ? s.addons : [];
  const packages = DEFAULT_PACKAGES.map(p => {
    const o = savedPk.find(x => x && x.id === p.id) || {};
    return {
      ...p,
      price: "price" in o ? money(o.price) : p.price,
      priceFrom: "priceFrom" in o ? money(o.priceFrom) : p.priceFrom
    };
  });
  const addons = DEFAULT_ADDONS.map(a => {
    const o = savedAo.find(x => x && x.id === a.id) || {};
    const r = money(o.rate);
    return { ...a, rate: r === null ? a.rate : r };
  });
  const mr = s.marketRates && Array.isArray(s.marketRates.rows) ? s.marketRates : DEFAULT_MARKET_RATES;
  return { packages, addons, marketRates: mr };
}

/** The values Settings saves. Same shape and ids as Phase 11. */
export function pricingToSettings(pricing) {
  return {
    packages: pricing.packages.map(p => ({ id: p.id, name: p.name, price: p.price, priceFrom: p.priceFrom })),
    addons: pricing.addons.map(a => ({ id: a.id, name: a.name, rate: a.rate, unit: a.unit })),
    marketRates: pricing.marketRates
  };
}

/** Fills in anything new and retires the pre-contract default terms. Returns null when nothing changes. */
export function upgradeSettings(prev) {
  const patch = {};
  if (!prev.paymentTerms || prev.paymentTerms === OLD_DEFAULT_TERMS.paymentTerms) patch.paymentTerms = DEFAULT_TERMS_TEXT.paymentTerms;
  if (!prev.cancellationPolicy || prev.cancellationPolicy === OLD_DEFAULT_TERMS.cancellationPolicy) patch.cancellationPolicy = DEFAULT_TERMS_TEXT.cancellationPolicy;
  return Object.keys(patch).length ? patch : null;
}

export const packagePriceLabel = p => p.price !== null ? "$" + p.price.toLocaleString("en-US")
  : p.priceFrom !== null ? "from $" + p.priceFrom.toLocaleString("en-US") + " (custom)" : "quoted case by case";

const unitLabel = u => u === "hour" ? "per hour" : u === "person" ? "per person" : "flat";

export function marketRatesText(mr) {
  const rows = (mr && mr.rows) || [];
  if (!rows.length) return "(No market-rate table saved. Say so in each custom line's market note and price conservatively.)";
  const range = r => {
    const lo = money(r.low), hi = money(r.high);
    if (lo !== null && hi !== null && lo !== hi) return "$" + lo.toLocaleString("en-US") + "–$" + hi.toLocaleString("en-US");
    const v = lo !== null ? lo : hi;
    return v !== null ? "$" + v.toLocaleString("en-US") : "no figure";
  };
  return rows.map(r => "- " + r.item + ": " + range(r) + " " + (r.unit || "") + (r.note ? " (" + r.note + ")" : "")).join("\n")
    + ((mr.region || mr.asOf) ? "\n(Region: " + (mr.region || "Central Coast") + (mr.asOf ? "; collected " + mr.asOf : "") + ")" : "");
}

export const QUOTE_SYSTEM = [
  "You help a one-woman wedding coordination studio on California's Central Coast price a proposal for one couple.",
  "Her packages and add-ons have fixed prices that you never change. Your job is narrow:",
  "(1) pick her package: use the couple's requested package; recommend one only if they asked for help choosing or gave none;",
  "(2) choose which of her add-ons their answers call for, with a quantity for hourly or per-person add-ons;",
  "(3) for Full-Service only, set the fee at or above its starting price, scaled to guest count, timeline and scope;",
  "(4) price any CUSTOM EXTRAS: things the couple asked for that her package does not include and no add-on covers",
  "(for example a welcome party, a farewell brunch, a second ceremony, design help on a non-design package, multi-day coverage).",
  "Price each custom extra against the Central Coast market-rate table you are given, never national averages.",
  "Never add a custom extra for something the package already includes or an add-on covers. If nothing calls for one, return none.",
  "Return STRICT JSON only, no prose and no markdown fences, in this shape:",
  '{"packageId": "wedding-day"|"partial"|"full-service", "fullServiceFee": number|null, "addons": [{"id": string, "qty": number, "note": string}], "custom": [{"label": string, "amount": number, "note": string, "market": string}], "rationale": string}',
  "Amounts are whole US dollars. qty is hours for per-hour add-ons, people for per-person add-ons, and 1 for flat ones.",
  "market: one or two sentences naming the table rows you compared against and the range they give. It is internal and never shown to the couple.",
  "rationale: two or three sentences on the package choice and anything you added, tied to the couple's answers. It is internal and never shown to the couple.",
  "Labels and notes may be shown to the couple, so write them in the studio's voice: plain, warm, a little dry. No exclamation points, no hype, never 'dream wedding', 'magical' or 'princess'.",
  "The studio's own upfront payment is a 'retainer', never a 'deposit'."
].join(" ");

export function buildQuotePrompt(clientSummary, requestedPackageId, pricing) {
  const L = [];
  L.push("HER PACKAGES");
  pricing.packages.forEach(p => {
    L.push("- " + p.id + " · " + p.name + " · " + packagePriceLabel(p));
    L.push("  Includes: " + p.inclusions.join("; "));
    L.push("  Not included: " + p.notIncluded);
  });
  L.push("");
  L.push("HER ADD-ONS (fixed rates)");
  pricing.addons.forEach(a => L.push("- " + a.id + " · " + a.name + " · $" + a.rate.toLocaleString("en-US") + " " + unitLabel(a.unit)
    + (a.includedIn ? " (already included in: " + a.includedIn.join(", ") + ")" : "")));
  L.push("");
  L.push("CENTRAL COAST MARKET RATES (for custom extras and the Full-Service fee)");
  L.push(marketRatesText(pricing.marketRates));
  L.push("");
  L.push("REQUESTED PACKAGE: " + (requestedPackageId || "none / help me choose"));
  L.push("");
  L.push("THE COUPLE");
  L.push(clientSummary);
  return { system: QUOTE_SYSTEM, prompt: L.join("\n") + "\n\nReturn strict JSON only." };
}

export function parseQuoteJSON(text) {
  if (!text || !text.trim()) throw new Error("The model returned an empty response. Try regenerating.");
  let s = text.trim();
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  const a = s.indexOf("{"), b = s.lastIndexOf("}");
  if (a === -1 || b === -1) throw new Error("The model didn't return a quote in the expected format. Try regenerating.");
  try { return JSON.parse(s.slice(a, b + 1)); } catch (e) { throw new Error("Couldn't read the quote the model returned. Try regenerating."); }
}

const str = (v, max) => String(v == null ? "" : v).trim().slice(0, max || 300);

/**
 * Turns the model's choices into line items at her prices. Her prices always win:
 * the package and add-ons are priced here, not by the model.
 */
export function assembleQuote(raw, pricing, requestedPackageId) {
  const r = raw && typeof raw === "object" ? raw : {};
  const ids = pricing.packages.map(p => p.id);
  const pkgId = requestedPackageId && ids.includes(requestedPackageId) ? requestedPackageId
    : ids.includes(r.packageId) ? r.packageId : "partial";
  const pkg = pricing.packages.find(p => p.id === pkgId);

  let pkgFee, pkgNote = "";
  if (pkg.price !== null) {
    pkgFee = pkg.price;
  } else {
    const floor = pkg.priceFrom || 0;
    const asked = money(r.fullServiceFee);
    pkgFee = Math.max(floor, asked === null ? floor : asked);
    pkgNote = "Priced to your guest count, timeline and scope";
  }
  const lineItems = [{ label: pkg.name, amount: pkgFee, note: pkgNote, kind: "package" }];

  const seen = new Set();
  (Array.isArray(r.addons) ? r.addons : []).forEach(x => {
    const a = pricing.addons.find(ad => ad.id === (x && x.id));
    if (!a || seen.has(a.id) || (a.includedIn || []).includes(pkg.id)) return;
    seen.add(a.id);
    const qty = a.unit === "flat" ? 1 : Math.min(40, Math.max(1, Math.round((Number(x.qty) || 1) * 2) / 2));
    const label = a.name + (a.unit === "hour" ? " (" + qty + (qty === 1 ? " hour)" : " hours)") : a.unit === "person" && qty > 1 ? " (" + qty + ")" : "");
    lineItems.push({ label, amount: Math.round(a.rate * qty), note: str(x.note, 200), kind: "addon" });
  });

  (Array.isArray(r.custom) ? r.custom : []).slice(0, 6).forEach(x => {
    const amount = money(x && x.amount);
    const label = str(x && x.label, 120);
    if (!label || amount === null || amount === 0) return;
    lineItems.push({ label, amount: Math.min(amount, 50000), note: str(x.note, 200), kind: "custom", market: str(x.market, 600) });
  });

  const base = lineItems.filter(li => li.kind !== "custom").reduce((s, li) => s + li.amount, 0);
  const all = lineItems.reduce((s, li) => s + li.amount, 0);
  return {
    packageId: pkg.id,
    packageTier: pkg.name,
    rangeLow: base,
    rangeHigh: all,
    lineItems,
    rationale: str(r.rationale, 1200)
  };
}
